/**
 * Team Service
 * Handles all team-related database operations
 */
import { 
  doc, 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  writeBatch, 
  serverTimestamp,
  deleteField,
  onSnapshot,
  orderBy,
  limit,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { EVALUATION_CRITERIA, calculateCriteriaTotal, validateCriteriaScores } from '../constants/evaluationCriteria';

const TEAMS_COLLECTION = 'teams';

/**
 * Generate a unique team ID
 * @returns {string} Unique team ID
 */
const generateTeamId = () => {
  return Date.now().toString(36).toUpperCase();
};

/**
 * Create a new team
 * @param {string} userId - Firebase user ID
 * @param {Object} teamData - Team data object
 * @returns {Promise<Object>} Result object with success status
 */
export const createTeam = async (userId, teamData) => {
  try {
    const teamId = generateTeamId();
    
    const team = {
      teamId,
      teamName: teamData.teamName,
      teamEmail: teamData.teamEmail,
      members: teamData.members,
      teamLead: teamData.members[0],
      status: 'Registered',
      registeredAt: new Date().toISOString(),
      createdAt: serverTimestamp()
    };

    await setDoc(doc(db, TEAMS_COLLECTION, userId), team);
    
    return { success: true, teamId, team };
  } catch (error) {
    console.error('Error creating team:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get team by user ID
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object>} Result object with team data
 */
export const getTeamByUserId = async (userId) => {
  try {
    const teamDoc = await getDoc(doc(db, TEAMS_COLLECTION, userId));
    
    if (teamDoc.exists()) {
      return { 
        success: true, 
        team: {
          id: teamDoc.id, // Include document ID for reference
          ...teamDoc.data()
        }
      };
    }
    
    return { success: false, error: 'Team not found' };
  } catch (error) {
    console.error('Error fetching team:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all teams
 * @returns {Promise<Object>} Result object with teams array
 */
export const getAllTeams = async () => {
  try {
    const teamsSnapshot = await getDocs(collection(db, TEAMS_COLLECTION));
    const teams = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, teams };
  } catch (error) {
    console.error('Error fetching teams:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team submission
 * @param {string} userId - Firebase user ID
 * @param {Object} submissionData - Submission data (driveLink, track)
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamSubmission = async (userId, submissionData) => {
  try {
    const teamDocRef = doc(db, TEAMS_COLLECTION, userId);
    
    const updateData = {
      driveLink: submissionData.driveLink,
      track: submissionData.track,
      ideaSubmittedAt: new Date().toISOString(),
      status: 'Submitted',
      // Reset edit permission and evaluation status on submission/resubmission
      canEdit: false,
      evaluationStatus: 'pending',
      score: null,
      scores: null,
      // Clear rejection/remarks data on resubmission
      isRemarked: false,
      remarks: null,
      remarksAddedAt: null,
      remarksAddedBy: null
    };
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Submission timeout after 30 seconds')), 30000);
    });
    
    const updatePromise = updateDoc(teamDocRef, updateData);
    await Promise.race([updatePromise, timeoutPromise]);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating submission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team status
 * @param {string} teamId - Team document ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamStatus = async (teamId, status) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      status,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating team status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team evaluation score
 * @param {string} teamId - Team document ID
 * @param {Object} scores - Score breakdown
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamScore = async (teamId, scores) => {
  try {
    const totalScore = Object.values(scores).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      evaluationStatus: 'evaluated',
      score: totalScore,
      scores,
      evaluatedAt: new Date().toISOString()
    });
    
    return { success: true, totalScore };
  } catch (error) {
    console.error('Error updating team score:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team edit permission
 * @param {string} teamId - Team document ID
 * @param {boolean} canEdit - Whether team can edit their submission
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamEditPermission = async (teamId, canEdit) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      canEdit,
      editPermissionUpdatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating team edit permission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team final round status (shortlisted for offline hackathon)
 * @param {string} teamId - Team document ID
 * @param {boolean} isShortlisted - Whether team is shortlisted for finals
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamFinalRound = async (teamId, isShortlisted) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      shortlistedForFinals: isShortlisted,
      shortlistedAt: isShortlisted ? new Date().toISOString() : null
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating team final round status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reject team with remarks (for access issues or other problems)
 * @param {string} teamId - Team document ID
 * @param {string} remarks - Rejection reason/remarks
 * @param {string} evaluatorEmail - Email of evaluator who is rejecting
 * @returns {Promise<Object>} Result object with success status
 */
export const rejectTeamWithRemarks = async (teamId, remarks, evaluatorEmail) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      isRemarked: true,
      remarks: remarks.trim(),
      remarksAddedAt: new Date().toISOString(),
      remarksAddedBy: evaluatorEmail,
      evaluationStatus: 'remarked',
      status: 'Rejected'
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error rejecting team with remarks:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team edit details permission (Admin controls this)
 * @param {string} teamId - Team document ID
 * @param {boolean} canEdit - Whether team can edit their details
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamEditDetailsPermission = async (teamId, canEdit) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      canEditTeamDetails: canEdit,
      editDetailsPermissionUpdatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating team edit details permission:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update team details (team name and members)
 * @param {string} userId - Firebase user ID
 * @param {Object} teamDetails - Team details object { teamName, members }
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTeamDetails = async (userId, teamDetails) => {
  try {
    const { teamName, members } = teamDetails;
    
    // Validation
    if (!teamName || !teamName.trim()) {
      return { success: false, error: 'Team name is required' };
    }
    
    if (!members || !Array.isArray(members) || members.length === 0) {
      return { success: false, error: 'At least one team member is required' };
    }
    
    if (members.length > 4) {
      return { success: false, error: 'Maximum 4 members allowed' };
    }
    
    // Validate each member
    for (let i = 0; i < members.length; i++) {
      const member = members[i];
      if (!member.name || !member.name.trim()) {
        return { success: false, error: `Member ${i + 1}: Name is required` };
      }
      if (!member.email || !member.email.trim()) {
        return { success: false, error: `Member ${i + 1}: Email is required` };
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(member.email)) {
        return { success: false, error: `Member ${i + 1}: Invalid email format` };
      }
      if (!member.usn || !member.usn.trim()) {
        return { success: false, error: `Member ${i + 1}: USN is required` };
      }
      if (!member.qualification || !member.qualification.trim()) {
        return { success: false, error: `Member ${i + 1}: Qualification is required` };
      }
      if (!member.college || !member.college.trim()) {
        return { success: false, error: `Member ${i + 1}: College is required` };
      }
      if (!member.year || !member.year.trim()) {
        return { success: false, error: `Member ${i + 1}: Year is required` };
      }
      if (!member.phone || !member.phone.trim()) {
        return { success: false, error: `Member ${i + 1}: Phone is required` };
      }
      if (!member.gender || !member.gender.trim()) {
        return { success: false, error: `Member ${i + 1}: Gender is required` };
      }
    }
    
    // Update only team name, members, and team lead
    // Do NOT touch submission-related fields (driveLink, track, status, scores, etc.)
    await updateDoc(doc(db, TEAMS_COLLECTION, userId), {
      teamName: teamName.trim(),
      members: members,
      teamLead: members[0], // First member is always team lead
      teamDetailsUpdatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating team details:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to real-time team data changes
 * @param {string} userId - Firebase user ID (document ID in teams collection)
 * @param {Function} callback - Callback function to handle team data updates
 * @returns {Function} Unsubscribe function to stop listening
 */
export const subscribeToTeamChanges = (userId, callback) => {
  try {
    const teamDocRef = doc(db, TEAMS_COLLECTION, userId);
    
    // Set up real-time listener
    const unsubscribe = onSnapshot(
      teamDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const teamData = {
            id: docSnapshot.id,
            ...docSnapshot.data()
          };
          callback(teamData);
        } else {
          console.warn('Team document does not exist');
          callback(null);
        }
      },
      (error) => {
        console.error('Error in team subscription:', error);
        callback(null);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up team subscription:', error);
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Subscribe to real-time changes for all teams
 * Used by Admin and Evaluator pages
 * @param {Function} callback - Callback function to handle teams data updates
 * @returns {Function} Unsubscribe function to stop listening
 */
export const subscribeToAllTeams = (callback) => {
  try {
    const teamsQuery = query(
      collection(db, TEAMS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    // Set up real-time listener for all teams
    const unsubscribe = onSnapshot(
      teamsQuery,
      (querySnapshot) => {
        const teams = [];
        querySnapshot.forEach((doc) => {
          teams.push({
            id: doc.id,
            ...doc.data()
          });
        });
        console.log('All teams updated in real-time:', teams.length, 'teams');
        callback(teams);
      },
      (error) => {
        console.error('Error in all teams subscription:', error);
        callback([]);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up all teams subscription:', error);
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Submit payment transaction ID
 * @param {string} userId - Firebase user ID (team document ID)
 * @param {string} transactionId - UPI transaction ID/UTR number
 * @returns {Promise<Object>} Result object with success status
 */
export const submitPaymentTransaction = async (userId, transactionId) => {
  try {
    if (!transactionId || !transactionId.trim()) {
      return { success: false, error: 'Transaction ID is required' };
    }

    await updateDoc(doc(db, TEAMS_COLLECTION, userId), {
      transactionId: transactionId.trim(),
      paymentStatus: 'pending',
      paymentSubmittedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error submitting payment transaction:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verify payment (Admin action)
 * @param {string} teamId - Team document ID
 * @param {string} adminEmail - Email of admin verifying the payment
 * @returns {Promise<Object>} Result object with success status
 */
export const verifyPayment = async (teamId, adminEmail) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      paymentStatus: 'verified',
      paymentVerifiedAt: new Date().toISOString(),
      paymentVerifiedBy: adminEmail
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error verifying payment:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Revoke payment verification (Admin action)
 * @param {string} teamId - Team document ID
 * @returns {Promise<Object>} Result object with success status
 */
export const revokePaymentVerification = async (teamId) => {
  try {
    await updateDoc(doc(db, TEAMS_COLLECTION, teamId), {
      paymentStatus: 'pending',
      paymentVerifiedAt: null,
      paymentVerifiedBy: null
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error revoking payment verification:', error);
    return { success: false, error: error.message };
  }
};

// ==========================================
// JURY EVALUATION FUNCTIONS
// Multi-round scoring system - SAFE (only adds new fields)
// ==========================================

/**
 * Submit evaluation score for a specific round
 * SAFE: Only adds to evaluations.roundX field, doesn't touch existing data
 */
export const submitRoundEvaluation = async (teamId, roundNumber, marks, evaluatorEmail, comments = '') => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    
    // Validate inputs
    if (!teamId || !roundNumber || marks === undefined || marks === null) {
      throw new Error('Invalid input parameters');
    }
    
    if (marks < 0 || marks > 100) {
      throw new Error('Marks must be between 0 and 100');
    }
    
    // Use dot notation to safely add nested field without affecting existing data
    const updateData = {
      [`evaluations.${roundNumber}.marks`]: Number(marks),
      [`evaluations.${roundNumber}.evaluatedBy`]: evaluatorEmail,
      [`evaluations.${roundNumber}.evaluatedAt`]: new Date().toISOString(),
      [`evaluations.${roundNumber}.comments`]: comments
    };
    
    await updateDoc(teamRef, updateData);
    
    // Recalculate rankings after score submission
    await calculateRankingsForRound(roundNumber);
    
    return { success: true, message: 'Evaluation submitted successfully' };
  } catch (error) {
    console.error('Error submitting evaluation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get evaluation scores for a specific team and round
 */
export const getRoundEvaluation = async (teamId, roundNumber) => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    const teamDoc = await getDoc(teamRef);
    
    if (!teamDoc.exists()) {
      return { success: false, error: 'Team not found' };
    }
    
    const teamData = teamDoc.data();
    const evaluation = teamData.evaluations?.[roundNumber] || null;
    
    return { success: true, evaluation };
  } catch (error) {
    console.error('Error getting evaluation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate and update rankings for a specific round
 * SAFE: Only updates rank fields
 */
export const calculateRankingsForRound = async (roundNumber) => {
  try {
    const teamsSnapshot = await getDocs(collection(db, TEAMS_COLLECTION));
    
    // Filter teams that have scores for this round
    const teamsWithScores = [];
    teamsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const marks = data.evaluations?.[roundNumber]?.marks;
      if (marks !== undefined && marks !== null) {
        teamsWithScores.push({
          id: doc.id,
          marks: marks
        });
      }
    });
    
    // Sort by marks (highest first)
    teamsWithScores.sort((a, b) => b.marks - a.marks);
    
    // Update ranks in batch
    const batch = writeBatch(db);
    teamsWithScores.forEach((team, index) => {
      const teamRef = doc(db, TEAMS_COLLECTION, team.id);
      batch.update(teamRef, {
        [`evaluations.${roundNumber}.rank`]: index + 1
      });
    });
    
    await batch.commit();
    
    return { success: true, teamsRanked: teamsWithScores.length };
  } catch (error) {
    console.error('Error calculating rankings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate overall rankings based on all three rounds
 * SAFE: Only adds overallScore and finalRank fields
 */
export const calculateOverallRankings = async () => {
  try {
    const teamsSnapshot = await getDocs(collection(db, TEAMS_COLLECTION));
    
    const teamsWithScores = [];
    teamsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const evaluations = data.evaluations || {};
      
      // Calculate total score from all rounds
      let totalScore = 0;
      let roundsCompleted = 0;
      
      ['round1', 'round2', 'round3'].forEach(round => {
        if (evaluations[round]?.marks !== undefined) {
          totalScore += evaluations[round].marks;
          roundsCompleted++;
        }
      });
      
      if (roundsCompleted > 0) {
        teamsWithScores.push({
          id: doc.id,
          totalScore: totalScore,
          roundsCompleted: roundsCompleted
        });
      }
    });
    
    // Sort by total score (highest first)
    teamsWithScores.sort((a, b) => b.totalScore - a.totalScore);
    
    // Update overall rankings in batch
    const batch = writeBatch(db);
    teamsWithScores.forEach((team, index) => {
      const teamRef = doc(db, TEAMS_COLLECTION, team.id);
      batch.update(teamRef, {
        overallScore: team.totalScore,
        finalRank: index + 1
      });
    });
    
    await batch.commit();
    
    return { success: true, teamsRanked: teamsWithScores.length };
  } catch (error) {
    console.error('Error calculating overall rankings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all teams with their evaluation data for rankings
 * Only includes teams that are shortlisted for finals
 */
export const getTeamsWithEvaluations = async () => {
  try {
    // Only get teams that are shortlisted for finals
    const teamsQuery = query(
      collection(db, TEAMS_COLLECTION),
      where('shortlistedForFinals', '==', true)
    );
    const teamsSnapshot = await getDocs(teamsQuery);
    const teams = teamsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, teams };
  } catch (error) {
    console.error('Error fetching teams with evaluations:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get rankings for a specific round
 */
export const getRankingsForRound = async (roundNumber) => {
  try {
    const teamsSnapshot = await getDocs(collection(db, TEAMS_COLLECTION));
    
    const rankedTeams = [];
    teamsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const evaluation = data.evaluations?.[roundNumber];
      
      if (evaluation?.marks !== undefined) {
        rankedTeams.push({
          id: doc.id,
          teamName: data.teamName,
          teamId: data.teamId,
          track: data.track,
          marks: evaluation.marks,
          // Don't use evaluation.rank as it may not be set
          evaluatedBy: evaluation.evaluatedBy,
          evaluatedAt: evaluation.evaluatedAt,
          // Include full evaluation data for details modal
          evaluations: data.evaluations || {},
          overallScore: data.overallScore,
          totalCombinedScore: data.totalCombinedScore,
          finalRank: data.finalRank
        });
      }
    });
    
    // Sort by marks (highest first) and assign ranks
    rankedTeams.sort((a, b) => b.marks - a.marks);
    
    // Assign ranks after sorting
    rankedTeams.forEach((team, index) => {
      team.rank = index + 1;
    });
    
    return { success: true, rankings: rankedTeams };
  } catch (error) {
    console.error('Error getting rankings:', error);
    return { success: false, error: error.message };
  }
};

// ==========================================
// TEAM SHORTLIST MANAGEMENT
// ==========================================

/**
 * Remove team from shortlist and clean up all evaluation data
 * Ensures team doesn't appear in rankings after removal
 */
export const removeFromShortlist = async (teamId) => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    
    // Clear evaluation data when removing from shortlist
    await updateDoc(teamRef, {
      shortlistedForFinals: false,
      evaluations: {}, // Clear all round evaluations
      overallScore: deleteField(), // Remove overall score
      finalRank: deleteField() // Remove final rank
    });
    
    // Recalculate rankings after removal
    await calculateOverallRankings();
    
    return { success: true, message: 'Team removed from shortlist successfully' };
  } catch (error) {
    console.error('Error removing team from shortlist:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add team to shortlist
 */
export const addToShortlist = async (teamId) => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    
    await updateDoc(teamRef, {
      shortlistedForFinals: true
    });
    
    return { success: true, message: 'Team added to shortlist successfully' };
  } catch (error) {
    console.error('Error adding team to shortlist:', error);
    return { success: false, error: error.message };
  }
};

// ==========================================
// CRITERIA-BASED EVALUATION FUNCTIONS
// ==========================================

/**
 * Submit evaluation with detailed criteria scores for a specific round
 * SAFE: Only adds to evaluations.roundX field with criteria structure
 */
export const submitCriteriaEvaluation = async (teamId, roundNumber, criteriaScores, evaluatorEmail, comments = '') => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    
    // Validate inputs
    if (!teamId || !roundNumber || !criteriaScores) {
      throw new Error('Invalid input parameters');
    }
    
    // Validate criteria scores
    const validation = validateCriteriaScores(criteriaScores, roundNumber);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }
    
    // Calculate total marks from criteria
    const totalMarks = calculateCriteriaTotal(criteriaScores, roundNumber);
    
    // Use dot notation to safely add nested fields
    const updateData = {
      // Store both criteria breakdown and total for compatibility
      [`evaluations.${roundNumber}.criteria`]: criteriaScores,
      [`evaluations.${roundNumber}.marks`]: totalMarks,
      [`evaluations.${roundNumber}.evaluatedBy`]: evaluatorEmail,
      [`evaluations.${roundNumber}.evaluatedAt`]: new Date().toISOString(),
      [`evaluations.${roundNumber}.comments`]: comments,
      [`evaluations.${roundNumber}.evaluationType`]: 'criteria'
    };
    
    await updateDoc(teamRef, updateData);
    
    // Recalculate rankings after score submission
    await calculateRankingsForRound(roundNumber);
    
    return { 
      success: true, 
      message: 'Criteria evaluation submitted successfully',
      totalMarks 
    };
  } catch (error) {
    console.error('Error submitting criteria evaluation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get evaluation with criteria scores for a specific team and round
 */
export const getCriteriaEvaluation = async (teamId, roundNumber) => {
  try {
    const teamRef = doc(db, TEAMS_COLLECTION, teamId);
    const teamDoc = await getDoc(teamRef);
    
    if (!teamDoc.exists()) {
      return { success: false, error: 'Team not found' };
    }
    
    const teamData = teamDoc.data();
    const evaluation = teamData.evaluations?.[roundNumber] || null;
    
    return { success: true, evaluation };
  } catch (error) {
    console.error('Error getting criteria evaluation:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all criteria evaluations for a specific round (for analytics)
 */
export const getCriteriaEvaluationsForRound = async (roundNumber) => {
  try {
    const teamsSnapshot = await getDocs(collection(db, TEAMS_COLLECTION));
    
    const evaluations = [];
    teamsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const evaluation = data.evaluations?.[roundNumber];
      
      if (evaluation?.criteria) {
        evaluations.push({
          id: doc.id,
          teamName: data.teamName,
          teamId: data.teamId,
          criteria: evaluation.criteria,
          marks: evaluation.marks,
          rank: evaluation.rank,
          evaluatedBy: evaluation.evaluatedBy,
          evaluatedAt: evaluation.evaluatedAt,
          comments: evaluation.comments
        });
      }
    });
    
    // Sort by rank
    evaluations.sort((a, b) => (a.rank || 999) - (b.rank || 999));
    
    return { success: true, evaluations };
  } catch (error) {
    console.error('Error getting criteria evaluations:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate criteria-wise averages for a round (for analytics)
 */
export const calculateCriteriaAverages = async (roundNumber) => {
  try {
    const result = await getCriteriaEvaluationsForRound(roundNumber);
    if (!result.success) {
      return result;
    }
    
    const evaluations = result.evaluations;
    const roundConfig = EVALUATION_CRITERIA[roundNumber];
    
    if (!roundConfig || evaluations.length === 0) {
      return { success: true, averages: {} };
    }
    
    // Calculate average for each criterion
    const averages = {};
    roundConfig.criteria.forEach(criterion => {
      const total = evaluations.reduce((sum, evaluation) => {
        return sum + (evaluation.criteria[criterion.name] || 0);
      }, 0);
      averages[criterion.name] = {
        label: criterion.label,
        average: total / evaluations.length,
        maxMarks: criterion.maxMarks,
        percentage: (total / evaluations.length / criterion.maxMarks) * 100
      };
    });
    
    return { success: true, averages, totalEvaluations: evaluations.length };
  } catch (error) {
    console.error('Error calculating criteria averages:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Migrate existing simple marks to criteria-based evaluation (backward compatibility)
 * This function distributes existing marks equally across all criteria
 */
export const migrateSimpleMarksToCriteria = async (teamId, roundNumber) => {
  try {
    const result = await getRoundEvaluation(teamId, roundNumber);
    if (!result.success || !result.evaluation) {
      return { success: false, error: 'No evaluation found to migrate' };
    }
    
    const evaluation = result.evaluation;
    
    // Check if already has criteria
    if (evaluation.criteria || evaluation.evaluationType === 'criteria') {
      return { success: true, message: 'Already has criteria evaluation' };
    }
    
    const roundConfig = EVALUATION_CRITERIA[roundNumber];
    if (!roundConfig) {
      return { success: false, error: 'Invalid round number' };
    }
    
    // Distribute marks equally across criteria
    const criteriaScores = {};
    const marksPerCriterion = Math.floor(evaluation.marks / roundConfig.criteria.length);
    const remainder = evaluation.marks % roundConfig.criteria.length;
    
    roundConfig.criteria.forEach((criterion, index) => {
      criteriaScores[criterion.name] = marksPerCriterion + (index < remainder ? 1 : 0);
    });
    
    // Update with criteria while preserving original data
    return await submitCriteriaEvaluation(
      teamId, 
      roundNumber, 
      criteriaScores, 
      evaluation.evaluatedBy, 
      evaluation.comments
    );
  } catch (error) {
    console.error('Error migrating to criteria:', error);
    return { success: false, error: error.message };
  }
};

export default {
  createTeam,
  getTeamByUserId,
  getAllTeams,
  updateTeamSubmission,
  updateTeamStatus,
  updateTeamScore,
  updateTeamEditPermission,
  updateTeamFinalRound,
  updateTeamEditDetailsPermission,
  updateTeamDetails,
  rejectTeamWithRemarks,
  subscribeToTeamChanges,
  subscribeToAllTeams,
  submitPaymentTransaction,
  verifyPayment,
  revokePaymentVerification,
  // Jury Evaluation Functions
  submitRoundEvaluation,
  getRoundEvaluation,
  calculateRankingsForRound,
  calculateOverallRankings,
  getTeamsWithEvaluations,
  getRankingsForRound,
  // Team Shortlist Management Functions
  removeFromShortlist,
  addToShortlist,
  // Criteria-based Evaluation Functions
  submitCriteriaEvaluation,
  getCriteriaEvaluation,
  getCriteriaEvaluationsForRound,
  calculateCriteriaAverages,
  migrateSimpleMarksToCriteria
};
